<script>
import GbfsStream from "@/components/Admin/GbfsStream.vue";
import AdminListActions from "@/components/Admin/ListActions.vue";
import RelationInput from "@/components/Forms/RelationInput.vue";
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import FormButtons from "@/components/shared/FormButtons.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column } from "@/components/shared/PaginatedTableColumns";
import { capitalize } from "@/helpers/filters";
import { del, get, put } from "@/requests/server";
import Vue from "vue";

export default Vue.extend({
  name: "GbfsDataset",
  components: {
    FormButtons,
    GbfsStream,
    RelationInput,
    AdminListActions,
    PaginatedTable,
    FormsValidatedInput,
  },
  props: {
    name: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      dataset: {},
      loaded: false,
      saving: false,
      loading: false,
      columns: [
        new Column("id", this.$t("lists.id"), "id", { showByDefault: false }),
        new Column(
          "community.name",
          capitalize(this.$t("communities.fields.community.name")),
          "text",
          {
            urlFct: (item) => `/admin/communities/${item.community.id}`,
          }
        ),
        Column.withoutFilter(
          "loanables_count",
          this.$t("views.admin.gbfs_dataset.columns.vehicle_count"),
          "number",
          {
            sortable: false,
          }
        ),
      ],
    };
  },
  computed: {
    isNew() {
      return this.name === "new";
    },
    communityFetchParams() {
      return {
        gbfs_dataset_name: this.name,
      };
    },
  },
  created() {
    if (this.name !== "new") {
      this.fetchDataset();
    } else {
      this.loaded = true;
    }
  },
  methods: {
    async fetchDataset() {
      this.loading = true;
      const { data } = await get(`gbfs_datasets/${this.name}`, {
        notifications: {
          action: this.$t("views.admin.gbfs_dataset.notifications.loading.action"),
          resourceName: this.$t("views.admin.gbfs_dataset.notifications.loading.resource"),
        },
        cleanupCallback: () => (this.loading = false),
      });

      this.dataset = data;
      this.loaded = true;
    },
    save() {
      if (this.isNew) {
        this.createDataset();
      } else {
        this.updateDataset();
      }

      this.$refs.validation.reset();
    },
    async reset() {
      await this.fetchDataset();
      this.$refs.validation.reset();
    },
    async updateDataset() {
      this.saving = true;
      const { data } = await put(`gbfs_datasets/${this.name}`, this.dataset, {
        notifications: {
          action: this.$t("views.admin.gbfs_dataset.notifications.save.action"),
          resourceName: this.$t("views.admin.gbfs_dataset.notifications.save.resource"),
          onSuccess: true,
        },
        cleanupCallback: () => (this.saving = false),
      });
      this.dataset = data;
    },
    async createDataset() {
      this.saving = true;
      const { data } = await put(`gbfs_datasets`, this.dataset, {
        notifications: {
          action: this.$t("views.admin.gbfs_dataset.notifications.create.action"),
          resourceName: this.$t("views.admin.gbfs_dataset.notifications.create.resource"),
          onSuccess: true,
        },
        cleanupCallback: () => (this.saving = false),
      });
      this.dataset = data;

      await this.$router.push(`/admin/gbfs_datasets/${data.name}`);
    },
    async removeCommunity(item) {
      await del(`/gbfs_dataset_communities/${item.id}`, {
        notifications: {
          action: this.$t("views.admin.gbfs_dataset.notifications.community.remove.action"),
          resourceName: this.$t("views.admin.gbfs_dataset.notifications.community.remove.resource"),
          onSuccess: true,
        },
      });
    },

    async addCommunity(community) {
      await put(
        "gbfs_dataset_communities",
        {
          community_id: community.id,
          gbfs_dataset_name: this.name,
        },
        {
          notifications: {
            action: this.$t("views.admin.gbfs_dataset.notifications.community.add.action"),
            resourceName: this.$t("views.admin.gbfs_dataset.notifications.community.add.resource"),
            onSuccess: true,
          },
        }
      );
      this.$refs.table.refresh();
    },
  },
});
</script>

<template>
  <layout-loading v-if="!loaded" />
  <validation-observer v-else v-slot="{ passes, pristine }" ref="validation">
    <b-form class="form" @submit.stop.prevent="passes(save)">
      <h1 v-if="isNew" class="mb-4">{{ $t("views.admin.gbfs_dataset.title.new") | capitalize }}</h1>
      <h1 v-else class="mb-4">{{ dataset.name | capitalize }}</h1>

      <forms-validated-input
        v-if="isNew"
        v-model="dataset.name"
        :label="$t('views.admin.gbfs_dataset.form.name.label')"
        type="text"
        :rules="{
          required: true,
          alpha_dash: true,
          excluded: 'new',
        }"
        name="name"
      />

      <forms-validated-input
        v-model="dataset.notes"
        :label="$t('views.admin.gbfs_dataset.form.notes.label')"
        type="textarea"
        name="notes"
      />

      <paginated-table
        v-if="!isNew"
        id="admin-gbfs-dataset-communities-table"
        ref="table"
        endpoint="gbfs_dataset_communities"
        :columns="columns"
        :label="$t('views.admin.gbfs_dataset.table.title')"
        :load-relations="['community']"
        :fetch-params="communityFetchParams"
        :show-generate-csv="false"
        show-action-column
        key-column="id"
      >
        <template #head-buttons>
          <relation-input
            :placeholder="$t('views.admin.gbfs_dataset.table.add_community')"
            class="add-community-input"
            :value="null"
            reset-after-select
            :query="{
              slug: 'communities',
              value: 'id',
              text: 'name',
              params: {
                '!gbfs_datasets.name': name,
              },
            }"
            name="add-dataset"
            @input="addCommunity"
          />
        </template>
        <template #cell(actions)="{ item }">
          <admin-list-actions
            slug="communities"
            :item-name="item.community.name"
            :item="item"
            :destroy-action="removeCommunity"
            :show-restore="false"
            :archive-label="$t('views.admin.gbfs_dataset.table.remove')"
            @change="$refs.table.refresh"
          />
        </template>
      </paginated-table>

      <template v-if="!isNew">
        <gbfs-stream :root="dataset.link" stream="system_information" />

        <gbfs-stream :root="dataset.link" stream="vehicle_types" />

        <gbfs-stream :root="dataset.link" stream="vehicle_status" />
      </template>

      <form-buttons :changed="!pristine" :saving="saving" :loading="loading" @reset="reset" />
    </b-form>
  </validation-observer>
</template>

<style lang="scss">
.add-community-input {
  width: 15rem;
}
</style>
